/**
 * Criacao de registros e leitura do Auth - o que o onboarding usa.
 *
 * Separado de `catalog.ts` de proposito: listar e alterar e rotina; CRIAR e o caminho por onde um
 * cliente novo entra na plataforma, e e o unico lugar que fala com o Power BI (`discover-report`)
 * e com a hierarquia do Auth.
 *
 * Todas as rotas de criacao respondem com envelope `{rows: [registro]}` - conferido em
 * `backend/app/api/routes/catalog.py`, nao deduzido.
 */

import { apiGet, apiPost, type Envelope } from './client'
import type { Client, Dataset, PowerBiConnection } from './types'

/** Organization do Auth: no negocio, "hospital", mas pode ser plano ou area. */
export interface AuthOrganization {
  id: string
  name: string
}

/**
 * Workspace do Auth: a PORTA DE ACESSO de um relatorio. Quem nao alcanca o workspace (pelo
 * profile) nao ve o relatorio, mesmo que o relatorio exista e esteja publicado.
 */
export interface AuthWorkspace {
  id: string
  name: string
  organization_id?: string | null
}

/** Profile do Auth: o papel. A policy de RLS liga relatorio + profile + role do Power BI. */
export interface AuthProfile {
  id: string
  name: string
  profile_code?: string
  workspace_ids?: string[]
}

export interface Snapshot {
  tenant: { id?: string; name?: string }
  organizations: AuthOrganization[]
  workspaces: AuthWorkspace[]
  profiles: AuthProfile[]
}

interface HierarquiaCrua {
  hierarchy?: Envelope<{
    tenant?: { id?: string; name?: string }
    organizations?: AuthOrganization[]
    workspaces?: AuthWorkspace[]
    profiles?: AuthProfile[]
  }>
}

/**
 * Tenant, organizations, workspaces e profiles do Auth, numa chamada.
 *
 * O backend so devolve o snapshot do PROPRIO tenant da sessao (403 caso contrario): identidade
 * vive no Auth, e o manager nao e janela para o tenant dos outros.
 */
export async function fetchSnapshot(tenantId: string): Promise<Snapshot> {
  const cru = await apiGet<HierarquiaCrua>(`/auth-catalog/tenant/${tenantId}/snapshot`)
  const raiz = cru.hierarchy?.rows[0]
  return {
    tenant: raiz?.tenant ?? {},
    organizations: raiz?.organizations ?? [],
    workspaces: raiz?.workspaces ?? [],
    profiles: raiz?.profiles ?? [],
  }
}

async function criar<T>(caminho: string, corpo: unknown): Promise<T | undefined> {
  return (await apiPost<Envelope<T>>(caminho, corpo)).rows[0]
}

export function criarCliente(authTenantId: string): Promise<Client | undefined> {
  return criar<Client>('/catalog/clients', { auth_tenant_id: authTenantId })
}

export function criarHospital(clientId: string, organizationId: string): Promise<unknown> {
  return criar('/catalog/hospitals', {
    client_id: clientId,
    auth_organization_id: organizationId,
  })
}

export function criarConexao(corpo: {
  client_id: string
  name: string
  connection_key: string
  microsoft_tenant_id: string
  entra_client_id: string
  secret_ref: string
}): Promise<PowerBiConnection | undefined> {
  return criar<PowerBiConnection>('/catalog/powerbi-connections', corpo)
}

export function criarDataset(corpo: {
  client_id: string
  powerbi_connection_id: string
  name: string
  powerbi_workspace_id: string
  powerbi_dataset_id: string
  description?: string | null
}): Promise<Dataset | undefined> {
  return criar<Dataset>('/catalog/datasets', corpo)
}

export function criarReport(corpo: Record<string, unknown>): Promise<unknown> {
  return criar('/catalog/reports', corpo)
}

export function criarPolicy(corpo: Record<string, unknown>): Promise<unknown> {
  return criar('/catalog/access-policies', corpo)
}

/** O que o Power BI sabe sobre o relatorio - ou o que da para ler do link, sem validar. */
export interface Descoberta {
  powerbi_report_id?: string
  powerbi_workspace_id?: string
  powerbi_dataset_id?: string | null
  name?: string | null
  embed_url?: string | null
  web_url?: string | null
  pages?: Array<{ name?: string; powerbi_page_name?: string; display_order?: number }>
}

/**
 * Consulta a REST API do Power BI: traz o embed URL e o datasetId REAIS.
 *
 * Diferenca que importa para quem publica: `embed_url` montado a partir do link e um chute que
 * funciona na maioria dos casos; o que vem daqui e o que o Power BI diz ser. Publicar com o chute
 * errado da erro so na hora em que o usuario final abre o relatorio.
 */
export function descobrirReport(connectionKey: string, link: string): Promise<Descoberta | undefined> {
  return criar<Descoberta>('/catalog/powerbi/discover-report', {
    connection_key: connectionKey,
    link,
  })
}

/** So le o link, sem falar com o Power BI. Serve quando a conexao ainda nao esta configurada. */
export function extrairDoLink(link: string): Promise<Descoberta | undefined> {
  return criar<Descoberta>('/catalog/parse-powerbi-link', { link })
}
